import { type ReactNode, useRef } from 'react';
import React, { useCallback } from 'react';
import { useRecoilValue, useRecoilState } from 'recoil';
import { GetColor, Icon, Tooltip, getHelpCenterLinks } from 'venn-ui-kit';
import styled from 'styled-components';
import ExpandCollapseButton from './ExpandCollapseButton';
import { useRedrawCharts } from 'venn-utils';
import { isReportState, studioLeftPanelOpen } from 'venn-state';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface SidePanelItem {
  key: string;
  text: string;
  labelComponent?: ReactNode;
  Component: ReactNode;
}

interface SidePanelRedesignProps {
  defaultKey: string;
  items: SidePanelItem[];
}

export const SidePanelRedesign = ({ defaultKey, items }: SidePanelRedesignProps) => {
  const [isOpen, setIsOpen] = useRecoilState(studioLeftPanelOpen);
  const isReport = useRecoilValue(isReportState);

  const onToggle = useCallback(() => {
    setIsOpen((currentState) => !currentState);
  }, [setIsOpen]);

  useRedrawCharts(isOpen);

  const headerElementRef = useRef<HTMLDivElement | null>(null);

  if (!isOpen) {
    return (
      <SidePanelWrapper style={{ paddingTop: '10px' }}>
        <ExpandCollapseButton isOpen={isOpen} isLeftOrientation onToggle={onToggle} />
      </SidePanelWrapper>
    );
  }

  return (
    <SidePanelWrapper>
      <Tabs className="h-full flex flex-col overflow-hidden" defaultValue={defaultKey}>
        <SidePanelHeader>
          <HeaderTabsWrapper>
            <HeaderTabs ref={headerElementRef}>
              <TabsList className="bg-white p-0 flex gap-[18px]">
                {items.map((item) => (
                  <TabsTrigger
                    className="h-[33px] px-0 pb-[8px] flex items-start text-venn-grey-80 hover:text-venn-grey-60 data-[state=active]:text-black font-bold text-[14px] border-0 data-[state=active]:border-solid data-[state=active]:border-b-[6px] data-[state=active]:border-venn-dark-blue"
                    value={item.key}
                    key={item.key}
                  >
                    {item.text}
                  </TabsTrigger>
                ))}
              </TabsList>
              <ButtonContainer>
                <Tooltip
                  usePortal
                  content={`Click to learn how to use Input Settings in ${isReport ? 'Report Lab' : 'Studio'}.`}
                >
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={getHelpCenterLinks().STUDIO_REPORT_LAB_AIS_FAQ_HREF}
                  >
                    <Icon type="circle-question" />
                  </a>
                </Tooltip>
                <ExpandCollapseButton isOpen={isOpen} isLeftOrientation onToggle={onToggle} />
              </ButtonContainer>
            </HeaderTabs>
          </HeaderTabsWrapper>
        </SidePanelHeader>
        {items.map((item) => (
          <TabsContent className="mt-0 overflow-y-auto overflow-x-hidden h-full" value={item.key} key={item.key}>
            {item.Component}
          </TabsContent>
        ))}
      </Tabs>
    </SidePanelWrapper>
  );
};

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: baseline;
  gap: 6px;
  a {
    color: unset;
  }
`;

const HeaderTabsWrapper = styled.div`
  position: relative;
  padding: 12px 12px 0 12px;
`;

const HeaderTabs = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
`;

export const SidePanelWrapper = styled.div`
  height: 100%;
  border: 1px solid ${GetColor.GreyScale.Grey30};
  background: ${GetColor.White};
  @media print {
    display: none;
  }
`;

export const SidePanelHeader = styled.div`
  grid-area: header;
  border-bottom: 1px solid ${GetColor.GreyScale.Grey30};
`;
